import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Lang, resolveLanguageUrls } from '../../widgets/headers/language-switcher/language-routes';

/**
 * The single source of truth for "which language is this request in".
 *
 * This exists to retire the `pages/` vs `pages-ar/` fork (D3). The two trees
 * are 195 and 156 components, and for many pages the only real difference
 * between a component and its `…ComponentAr` twin is the locale segment on its
 * API calls - `menus/header/en` against `menus/header/ar`. Sitemap was the
 * first proven case: identical templates apart from whitespace, and a
 * stylesheet that differed only by RTL mirroring, which belongs in
 * raksme-rtl.css rather than in a duplicated component.
 *
 * Language is resolved with `resolveLanguageUrls`, the same pure function the
 * language switcher uses. That matters: several components decide language
 * with `router.url.includes('/ar')`, which is a substring test and reports
 * Arabic for English URLs like `/business-directory/12/architects`. The
 * resolver does an exact segment match instead.
 *
 * Deliberately has no dictionary loading and no new dependency. The codebase
 * already carries a working `t(key)` pattern - `i18n[lang][key]` falling back
 * to English and then to the key itself - so Path A does not need
 * ngx-translate, which on Angular 9 would mean pinning an old release.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {

  constructor(private router: Router) { }

  /** 'ar' only when a URL segment is exactly `ar`. */
  get lang(): Lang {
    return resolveLanguageUrls(this.router.url).lang;
  }

  get isRtl(): boolean {
    return this.lang === 'ar';
  }

  /**
   * Append the locale segment an API path expects: `api('menus/header')` gives
   * `menus/header/en` or `menus/header/ar`.
   *
   * This is the call that lets one component serve both languages, which is
   * the whole of the fork for pages whose markup does not differ.
   */
  api(base: string): string {
    return base.replace(/\/+$/, '') + '/' + this.lang;
  }

  /**
   * Read a CMS content field, in the language of the current route.
   *
   * The API does not translate field *names*: a page fetched from `pages/ar`
   * still carries `photo_gallery_title` alongside `photo_gallery_title_ar`,
   * and the Arabic component read the `_ar` key while the English one read the
   * base key. That split is a second reason the two trees existed, separate
   * from the locale on the request, and it would otherwise have to be
   * re-solved in every component the fork retires.
   *
   * Falls back to the base key when no `_ar` variant is present, which is the
   * common case - most fields are not duplicated - and then to ''. So a
   * missing translation shows the English text rather than a blank region.
   */
  field(content: any, key: string): any {
    if (!content) { return ''; }
    if (this.lang === 'ar') {
      const ar = content[key + '_ar'];
      if (ar !== undefined && ar !== null && String(ar).trim() !== '') { return ar; }
    }
    const base = content[key];
    return base === undefined || base === null ? '' : base;
  }

  /**
   * Look a key up in a component-supplied dictionary.
   *
   * Falls back to English and then to the key, so a missing translation shows
   * a readable string rather than a blank - the same contract the existing
   * in-component `t()` already uses, promoted here so it stops being
   * re-implemented per page.
   */
  t(dict: any, key: string): string {
    if (!dict) { return key; }
    const here = dict[this.lang] && dict[this.lang][key];
    if (here) { return here; }
    const en = dict.en && dict.en[key];
    return en ? en : key;
  }
}
